import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Film, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input, Select } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ViewToggle } from '@/components/ui/view-toggle';
import { useRowSelection } from '@/hooks/useRowSelection';
import {
  CONTENT_LIST_POLL_MS,
  CONTENT_STATUS_OPTIONS,
  CONTENT_STATUS_TONE,
  PERSONAS_COMPACT_LIMIT,
} from '@/constants/content';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatCost, shortId } from '@/lib/format';
import type { Content } from '@/types/content';
import type { BatchOp, TrashView } from '@/types/common';

type SortField = 'created' | 'cost' | 'duration';
type SortDir = 'asc' | 'desc';

export function ContentListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [view, setView] = useState<TrashView>('active');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [status, setStatus] = useState('all');
  const [sortBy, setSortBy] = useState<SortField>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const selection = useRowSelection();

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(id);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, status, sortBy, sortDir, pageSize, view]);

  useEffect(() => {
    selection.clear();
  }, [view, selection.clear]);

  const contentQ = useQuery({
    queryKey: ['content', 'paginated', view, debouncedQ, status, sortBy, sortDir, page, pageSize],
    queryFn: () => {
      const params: {
        q?: string;
        status?: string;
        sort_by: string;
        sort_dir: string;
        page: number;
        page_size: number;
        view: TrashView;
      } = {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
        view,
      };
      if (debouncedQ) params.q = debouncedQ;
      if (status !== 'all') params.status = status;
      return api.listContentPaginated(params);
    },
    refetchInterval: CONTENT_LIST_POLL_MS,
    placeholderData: (previousData) => previousData,
  });

  const batchM = useMutation({
    mutationFn: ({ ids, op }: { ids: string[]; op: BatchOp }) => api.batchContent(ids, op),
    onSuccess: (res, { op }) => {
      const verb = op === 'restore' ? 'restored' : op === 'purge' ? 'permanently deleted' : 'moved to Trash';
      toast.success(`${res.affected} content piece${res.affected === 1 ? '' : 's'} ${verb}`);
      setPurgeOpen(false);
      selection.clear();
      void queryClient.invalidateQueries({ queryKey: ['content'] });
    },
    onError: (err) => toast.error('Bulk action failed', { description: String(err) }),
  });

  const runBatch = (op: BatchOp) => {
    const ids = [...selection.selectedIds];
    if (ids.length === 0) return;
    if (op === 'purge') {
      setPurgeOpen(true);
      return;
    }
    batchM.mutate({ ids, op });
  };
  const personasQ = useQuery({
    queryKey: ['personas', 'compact'],
    queryFn: () => api.listPersonas({ limit: PERSONAS_COMPACT_LIMIT }),
  });

  const personaName = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of personasQ.data ?? []) {
      m.set(p.persona_id, p.voice_sheet?.display_name ?? p.persona_id);
    }
    return m;
  }, [personasQ.data]);

  const rows = contentQ.data?.items ?? [];
  const totalCount = contentQ.data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    if (!contentQ.data) return;
    if (page > totalPages) setPage(totalPages);
  }, [contentQ.data, page, totalPages]);

  const toggleSort = (field: Exclude<SortField, 'created'>) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const sortHeader = (label: string, field: Exclude<SortField, 'created'>) => {
    const active = sortBy === field;
    const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className="inline-flex cursor-pointer items-center gap-1 hover:text-foreground"
      >
        {label}
        <Icon
          className={cn('h-3 w-3', active ? 'text-foreground' : 'text-muted-foreground/50')}
        />
      </button>
    );
  };

  const columns: Column<Content>[] = [
    {
      key: 'thumb',
      header: '',
      width: '88px',
      cell: (c) => (
        <video
          src={api.videoUrl(c.content_id)}
          className="h-12 w-20 rounded-md border border-border bg-muted object-cover"
          muted
          preload="metadata"
        />
      ),
    },
    {
      key: 'caption',
      header: 'Caption',
      cell: (c) => (
        <div>
          <div className="line-clamp-2 text-sm">{c.script?.caption?.slice(0, 120) ?? '—'}</div>
          <div className="font-mono text-xs text-muted-foreground">{shortId(c.content_id)}</div>
        </div>
      ),
    },
    {
      key: 'persona',
      header: 'Persona',
      cell: (c) => (
        <Link
          to="/personas/$personaId"
          params={{ personaId: c.persona_id }}
          className="text-sm hover:underline"
        >
          {personaName.get(c.persona_id) ?? shortId(c.persona_id)}
        </Link>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (c) =>
        c.status ? (
          <Badge tone={CONTENT_STATUS_TONE[c.status] ?? 'neutral'}>{c.status}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'duration',
      header: sortHeader('Duration', 'duration'),
      cell: (c) => <span className="text-xs">{c.video?.duration_sec?.toFixed(1) ?? '—'}s</span>,
    },
    {
      key: 'cost',
      header: sortHeader('Cost', 'cost'),
      cell: (c) => <span className="text-xs">{formatCost(c.video?.cost_usd_est)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Content</h1>
          <p className="text-sm text-muted-foreground">
            UGC pieces produced by the content workflow. {rows.length} of {totalCount} shown.
          </p>
        </div>
        <Link to="/triggers/content">
          <Button>Generate content</Button>
        </Link>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <ViewToggle value={view} onChange={setView} />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search caption, brief, persona, provider, IDs…"
          className="max-w-sm"
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="max-w-[200px]"
        >
          {CONTENT_STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
      </div>

      {selection.count > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-2.5">
          <span className="text-sm font-medium">{selection.count} selected</span>
          <div className="ml-auto flex items-center gap-2">
            {view === 'active' ? (
              <Button
                variant="destructive"
                size="sm"
                disabled={batchM.isPending}
                onClick={() => runBatch('delete')}
              >
                <Trash2 className="h-4 w-4" /> Move to Trash
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={batchM.isPending}
                  onClick={() => runBatch('restore')}
                >
                  <RotateCcw className="h-4 w-4" /> Restore
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={batchM.isPending}
                  onClick={() => runBatch('purge')}
                >
                  <Trash2 className="h-4 w-4" /> Delete forever
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {contentQ.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(c) => c.content_id}
          selection={{
            selectedIds: selection.selectedIds,
            onToggleRow: selection.toggleRow,
            onToggleAll: () => selection.toggleAll(rows.map((c) => c.content_id)),
          }}
          onRowClick={(c) => {
            void navigate({ to: '/content/$contentId', params: { contentId: c.content_id } });
          }}
          empty={
            <EmptyState
              icon={<Film className="h-8 w-8" />}
              title="No content yet"
              description="Generate a content piece against an existing persona to see it here."
              action={
                <Link to="/triggers/content">
                  <Button>Generate content</Button>
                </Link>
              }
            />
          }
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Page size</span>
            <Select
              value={String(pageSize)}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="w-[96px]"
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </div>
          <div className="text-muted-foreground">
            Page {page} of {totalPages} · {totalCount} total
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={contentQ.isFetching || page <= 1}
          >
            Previous
          </Button>
          <Input
            type="number"
            min={1}
            max={totalPages}
            value={page}
            onChange={(e) => setPage(Math.min(totalPages, Math.max(1, Number(e.target.value) || 1)))}
            className="w-20"
          />
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={contentQ.isFetching || page >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      {purgeOpen && (
        <ConfirmDialog
          title="Delete forever?"
          description={`Permanently delete ${selection.count} content piece${
            selection.count === 1 ? '' : 's'
          } and their video files. This cannot be undone.`}
          confirmLabel="Delete forever"
          destructive
          pending={batchM.isPending}
          onConfirm={() => batchM.mutate({ ids: [...selection.selectedIds], op: 'purge' })}
          onCancel={() => setPurgeOpen(false)}
        />
      )}
    </div>
  );
}
